/**
 * Hook that encapsulates the return-to-app interstitial state.
 *
 * Detects whether the SPA is running in an external mobile browser after a
 * payment redirect and, if so, provides the interstitial element to render
 * and a flag to gate downstream side-effects (e.g. navigation, modals).
 *
 * The detection runs once on mount via lazy `useState` initialisers so that
 * subsequent renders do not re-evaluate `window.location` or the user agent.
 */

import { useState } from 'react';
import { buildReturnToAppUrl, shouldShowReturnToAppInterstitial } from 'utils/deep-link';
import ReturnToAppInterstitial from 'components/ReturnToAppInterstitial';

type Variant = 'success' | 'cancel';

interface ReturnToAppInterstitialResult {
  /** True while the interstitial should block other UI / side-effects */
  active: boolean;
  /** The interstitial element to render, or `null` when not applicable */
  element: React.ReactElement | null;
}

/**
 * Returns interstitial state for the given payment variant and optional
 * deep-link target path.
 *
 * @param variant  Payment outcome — controls the interstitial heading.
 *                 Pass `null` when no payment signal is present; the
 *                 interstitial will be inactive regardless of platform.
 * @param targetPath Optional path + search for the deep link (e.g.
 *                   `/trades/42?fromPayment=success`). When omitted the
 *                   current `window.location` is used.
 */
export default function useReturnToAppInterstitial(
  variant: Variant | null,
  targetPath?: string,
): ReturnToAppInterstitialResult {
  // captured once on mount — intentionally ignores subsequent targetPath changes
  const [shouldShow] = useState(() => shouldShowReturnToAppInterstitial());
  const [returnUrl] = useState(() => buildReturnToAppUrl(targetPath));
  const [dismissed, setDismissed] = useState(false);

  const active = !!variant && shouldShow && !!returnUrl && !dismissed;

  const element = active ? (
    <ReturnToAppInterstitial returnUrl={returnUrl!} onContinueInBrowser={() => setDismissed(true)} variant={variant!} />
  ) : null;

  return { active, element };
}
